import AnimationClip from '../AnimationClip';
import Pet from '../../../Prefabs/Pet/Pet';
import Point, { IPointData } from '../../../Utility/Point';
import { PetAnimations } from '../../../Utility/Helpers/Pets/PetAnimations';
import { AwaitIterators, ConcatIterators } from '../AnimationHelper';
import TransformComponent from '../../../Component/Transform';

export interface IMovePetMeta {
    points: Point[];
    exitAnimations?: PetAnimations[];
}

// Time it takes for one walk animation to complete
const timePerWalkAnimation = 1250;
/**
 * A number choosen by trial and error to get the amount of distance to travel per walk animation
 * adjusting to be high/lower will decrease/increase the pet's velocity
 */
const distancePerAnimation = 220;
/**
 * A number choosen by trial and error to get the amount of distance to travel per walk animation
 * adjusting to be high/lower will decrease/increase the pet's velocity
 */
const lastTweenDurationOffset = 300;
export class MovePet extends AnimationClip<IMovePetMeta> {
    public *execute(
        gameObject: Pet,
        meta: IMovePetMeta
    ): IterableIterator<number> {
        const { points, exitAnimations = [] } = meta;

        const transform = gameObject.getComponent(TransformComponent);
        const currentPos = transform.position.clone() as Point;
        const allPoints = [currentPos, ...points];

        // Used to calculate the duration of the tweens between each point
        const distanceBetweenPoints: number[] = [];
        let totalDistance = 0;
        for (let i = 1; i < allPoints.length; i++) {
            const distance = Point.distance(allPoints[i - 1], allPoints[i]);
            distanceBetweenPoints.push(distance);
            totalDistance += Math.abs(distance);
        }

        // Total number of walk animations required to animate across the entire distance
        const totalAnimationsRequired = getAnimationsRequiredForDistance(
            totalDistance
        );
        const totalWalkingDuration = (totalAnimationsRequired * timePerWalkAnimation) - lastTweenDurationOffset;

        // Get an array of required PetAnimations to reach walking state
        const toWalkingAnimations = gameObject.toWalkingAnimations();

        // Animate to a ready to walk stage
        let toWalking = this.chainDBAnimations(
            undefined,
            toWalkingAnimations.map(animation =>
                gameObject.getPlayAnimation(animation)
            )
        );

        yield* waitFor(toWalking);

        // Get tweens to each point
        const walkTweens: IterableIterator<number>[] = [];
        points.forEach((to, index) => {
            const from = (index === 0) ? (currentPos as Point) : points[index - 1];
            // Get % of total distance needed to cover to get to the point
            const percOfTotalDistance = distanceBetweenPoints[index] / totalDistance;
            // Get travel time to next point based off % of total distance * the total time spent walking
            const travelTimeBetweenPoints = (percOfTotalDistance * totalWalkingDuration);
            
            walkTweens.push(
                this.moveToPoint(transform, from, to, travelTimeBetweenPoints)
            );
        });

        // Get all walk animations required
        const walkAnimaitons: Array<() => Promise<void>> = new Array(
            totalAnimationsRequired
        )
            .fill(undefined)
            .map(value => gameObject.getPlayAnimation(PetAnimations.WALKING));

        let walking = this.chainDBAnimations(undefined, walkAnimaitons);

        yield* AwaitIterators([
            // Concat tweens from different points
            ConcatIterators(walkTweens),
            waitFor(walking),
        ]);

        // Apply any final animations to get to desired state
        let lastAnimation = this.chainDBAnimations(
            walking,
            exitAnimations.map(animation =>
                gameObject.getPlayAnimation(animation)
            )
        );
        yield* waitFor(lastAnimation);

        return;

        function* waitFor(animation: Promise<void>): IterableIterator<number> {
            let keepHolding: boolean = true;

            animation.then(() => {
                keepHolding = false;
            });

            while (keepHolding) {
                yield;
            }
        }

        function getAnimationsRequiredForDistance(distance: number) {
            return Math.max(1, Math.round(distance / distancePerAnimation));
        }
    }

    private moveToPoint(
        transform: TransformComponent,
        from: Point,
        to: Point,
        duration: number = 50
    ): IterableIterator<number> {
        const translateTween = this.getTween({ x: from.x, y: from.y })
            .to({ x: to.x, y: to.y }, duration)
            .onUpdate(onUpdate);

        return AwaitIterators([this.startTween(translateTween)]);

        function onUpdate(pos: IPointData) {
            transform.position.set(pos.x, pos.y);
        }
    }

    private chainDBAnimations(
        initial: Promise<void>,
        animations: Array<() => Promise<void>>
    ): Promise<void> {
        let animation = initial ?? new Promise(resolve => resolve());
        for (let i = 0; i < animations.length; i++) {
            animation = animation.then(animations[i]);
        }
        return animation;
    }
}
